import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

const INSTALL_READY_EVENT = "kaysou-assist-install-ready";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
}

function isAppleDevice() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
}

export function KaysouAssistInstallButton() {
  const [installPrompt, setInstallPrompt] = useState(
    () => window.__kaysouAssistInstallPrompt || null
  );
  const [installed, setInstalled] = useState(isStandalone);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const capturePrompt = (event) => {
      event.preventDefault();
      window.__kaysouAssistInstallPrompt = event;
      setInstallPrompt(event);
    };
    const captureEarlyPrompt = () => {
      setInstallPrompt(window.__kaysouAssistInstallPrompt || null);
    };
    const markInstalled = () => {
      window.__kaysouAssistInstallPrompt = null;
      setInstallPrompt(null);
      setInstalled(true);
      setShowInstructions(false);
    };

    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener(INSTALL_READY_EVENT, captureEarlyPrompt);
    window.addEventListener("appinstalled", markInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", capturePrompt);
      window.removeEventListener(INSTALL_READY_EVENT, captureEarlyPrompt);
      window.removeEventListener("appinstalled", markInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = installPrompt || window.__kaysouAssistInstallPrompt;
    if (!prompt) {
      setShowInstructions(true);
      return;
    }

    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice?.outcome === "accepted") {
        setInstalled(true);
      }
    } catch (error) {
      console.warn("[KAYSOU_ASSIST_PWA] native install unavailable", error);
      setShowInstructions(true);
    } finally {
      window.__kaysouAssistInstallPrompt = null;
      setInstallPrompt(null);
    }
  };

  if (installed) return null;

  const apple = isAppleDevice();

  return (
    <>
      <button
        type="button"
        onClick={handleInstall}
        className="px-3 h-9 rounded-lg border border-emerald-500 text-emerald-300 text-sm font-bold flex items-center gap-1.5"
        data-testid="kaysou-assist-install"
      >
        <Download className="w-4 h-4" />
        Instalar app
      </button>

      {showInstructions && (
        <div
          className="fixed inset-0 z-[1200] bg-black/70 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Instalar Kaysou Assist"
          data-testid="kaysou-assist-install-instructions"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white text-gray-900 p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-black">Instalar Kaysou Assist</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Instale no tablet para abrir como aplicativo.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowInstructions(false)}
                className="p-1 text-gray-500"
                aria-label="Fechar"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mt-5 rounded-xl bg-gray-100 p-4 text-sm space-y-3">
              {apple ? (
                <>
                  <p className="font-bold">No iPhone ou iPad, abra no Safari:</p>
                  <p className="flex items-center gap-2">
                    <Share className="w-5 h-5 text-blue-600" />
                    1. Toque em Compartilhar.
                  </p>
                  <p>2. Toque em “Adicionar à Tela de Início”.</p>
                  <p>3. Confirme em “Adicionar”.</p>
                </>
              ) : (
                <>
                  <p className="font-bold">No Android ou tablet:</p>
                  <p>1. Abra o menu do navegador (⋮).</p>
                  <p>2. Toque em “Instalar aplicativo” ou “Adicionar à tela inicial”.</p>
                  <p>3. Confirme em “Instalar”.</p>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowInstructions(false)}
              className="mt-5 w-full h-11 rounded-xl bg-emerald-600 text-white font-bold"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
}

